import SeleccionPOPago from './SeleccionPOPago';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../services/api';

const toDateInputValue = (value) => {
  if (!value) return '';

  const text = String(value).trim();
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return isoMatch[0];

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const round2 = (n) => Number((Number(n || 0) || 0).toFixed(2));

// Tipos ligados a facturas (ya SIN NC)
const TIPOS_FACTURA = ['PG', 'ND', 'RT'];
// Tipos libres (no requieren facturas seleccionadas)
const TIPOS_LIBRES = ['PP', 'SI'];

function ModalMovimiento({
  show,
  onClose,
  tipoMovimiento, // 'C' | 'P'
  idSeleccionado, // idtercero
  movimientoParaEditar, // { id, tipoDocumento, fecha, valorTotal, observaciones, idbanco, ... }
  facturasSeleccionadas = [], // [{ idfactura, numero_factura, saldo/amount_due/valorpago... }]
  tiposPermitidos
}) {
  const [tipoDocumento, setTipoDocumento] = useState('PG');
  const [usarPO,setUsarPO]=useState(false);
  const [facturasPO,setFacturasPO]=useState([]);
  const [guardandoPO,setGuardandoPO]=useState(false);
  const modoPO=usarPO && tipoMovimiento==='C' && tipoDocumento==='PG' && !movimientoParaEditar;
  const totalPO=facturasPO.reduce((s,f)=>s+f.seleccion.reduce((t,a)=>t+Math.round(Number(a.valor||0)*100),0),0)/100;
  const [fecha, setFecha] = useState('');
  const [valor, setValor] = useState('');
  const [observaciones, setObservaciones] = useState('');

  // ====== SI (manual / import) ======
  const [numeroFacturaSI, setNumeroFacturaSI] = useState('');
  const [usarImportador, setUsarImportador] = useState(false);
  const [archivoImport, setArchivoImport] = useState(null);
  const [importando, setImportando] = useState(false);
  const [resultadoImport, setResultadoImport] = useState(null);

  // ====== Banco (solo PG/PP) ======
  const [bancos, setBancos] = useState([]);
  const [idBanco, setIdBanco] = useState(null);
  const [costoBancario, setCostoBancario] = useState('');
  const [numeroComprobante, setNumeroComprobante] = useState('');
  const [detalleFacturasEdit, setDetalleFacturasEdit] = useState([]);
  const [detalleEditCargado, setDetalleEditCargado] = useState(false);
  const [cargandoEdicion, setCargandoEdicion] = useState(false);
  const [terceroEdicion, setTerceroEdicion] = useState(null);
  const [saldoFavorNcDisponible, setSaldoFavorNcDisponible] = useState(0);
  const [usarSaldoFavorNc, setUsarSaldoFavorNc] = useState(true);

  // Opción: descargar/guardar PDF del comprobante al guardar
  const [autoDescargar, setAutoDescargar] = useState(true);

  // ===== Helpers de saldo =====
  const getSaldoFactura = useCallback(
    (f) =>
      Number(
        f.saldo ?? f.amount_due ?? f.valorpago ?? f.valor_pendiente ?? f.valorpendiente ?? 0
      ) || 0,
    []
  );
  const totalSaldoSeleccion = useCallback(
    (arr) => (arr || []).reduce((s, f) => s + getSaldoFactura(f), 0),
    [getSaldoFactura]
  );

  const esEdicion = Boolean(movimientoParaEditar);
  const listaTipos = useMemo(() => {
    const base =
      Array.isArray(tiposPermitidos) && tiposPermitidos.length
        ? tiposPermitidos
        : facturasSeleccionadas.length > 0
          ? TIPOS_FACTURA
          : TIPOS_LIBRES;
    const actual = String(movimientoParaEditar?.tipoDocumento || '').toUpperCase();
    return actual && !base.includes(actual) ? [actual, ...base] : base;
  }, [tiposPermitidos, facturasSeleccionadas.length, movimientoParaEditar?.tipoDocumento]);

  const titulo = esEdicion
    ? `✏️ Editar Movimiento (${tipoMovimiento === 'C' ? 'Cliente' : 'Proveedor'})`
    : `➕ Nuevo Movimiento (${tipoMovimiento === 'C' ? 'Cliente' : 'Proveedor'})`;

  // ===== Estilos mínimos =====
  const styles = {
    grid: { display: 'grid', gap: 12 },
    two: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
    label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 },
    input: { height: 36, padding: '8px 10px' },
    number: { height: 36, padding: '8px 10px', textAlign: 'right' },
    textarea: { minHeight: 96, padding: 10, resize: 'vertical', lineHeight: 1.35 },
    hintRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: 6
    },
    hint: { fontSize: 12, color: '#666' },
    chips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
    chip: {
      fontSize: 12,
      padding: '6px 10px',
      borderRadius: 999,
      border: '1px solid #d0d0d0',
      background: '#f7f7f7',
      color: '#333',
      cursor: 'pointer'
    },
    preview: {
      marginTop: 8,
      background: '#f6f9ff',
      border: '1px solid #dbe7ff',
      padding: 10,
      borderRadius: 8,
      fontSize: 13,
      color: '#2a3a55'
    }
  };

  // ===== Chips / snippets (ya sin NC) =====
  const CHIP_SETS = {
    PG: ['Transferencia', 'Efectivo', 'Cheque #', 'Banco: ', 'Ref: ', 'Comprobante: '],
    ND: ['Intereses', 'Ajuste', 'Servicios adicionales', 'ND N° '],
    RT: ['Fuente', 'IVA', 'Régimen: ', 'Comprobante: '],
    PP: ['Anticipo', 'Aplicable a futuras facturas', 'Ref: '],
    SI: ['Saldo inicial por migración', 'Periodo: ', 'N° doc: ']
  };
  const chips = CHIP_SETS[tipoDocumento] || [];
  const insertSnippet = (txt) =>
    setObservaciones((prev) =>
      !prev ? txt : prev.trim().endsWith(';') ? prev + ' ' + txt : prev + '; ' + txt
    );

  // ===== Observaciones =====
  const MAX_OBS = 280;
  const normalizeSpaces = (s) =>
    s
      .replace(/\s+/g, ' ')
      .replace(/\s*,\s*/g, ', ')
      .replace(/\s*;\s*/g, '; ')
      .trim();
  const beautify = (s) => {
    const t = normalizeSpaces(s);
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
  };

  // ===== Init =====
  useEffect(() => {
    if (!show) return;

    const hoy = new Date().toISOString().slice(0, 10);

    // Resets básicos al abrir
    setFecha(hoy);
    setResultadoImport(null);
    setArchivoImport(null);
    setUsarImportador(false);
    setNumeroFacturaSI('');

    // Resets de banco/comisión/comprobante
    setIdBanco(null);
    setCostoBancario('');
    setNumeroComprobante('');
    setDetalleFacturasEdit([]);
    setDetalleEditCargado(false);
    setCargandoEdicion(false);
    setTerceroEdicion(null);
    setSaldoFavorNcDisponible(0);
    setUsarSaldoFavorNc(true);
    setAutoDescargar(true);

    // Cargar catálogo de bancos
    api
      .get('/api/catalogo?categoria=bancos')
      .then((res) => setBancos(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBancos([]));

    // ===== MODO EDICIÓN =====
    if (esEdicion && movimientoParaEditar) {
      setTipoDocumento((movimientoParaEditar.tipoDocumento || 'PG').toUpperCase());
      setFecha(toDateInputValue(movimientoParaEditar.fecha) || hoy);
      setValor(
        movimientoParaEditar.valorTotal != null
          ? String(Number(movimientoParaEditar.valorTotal))
          : ''
      );
      setObservaciones(movimientoParaEditar.observaciones || '');
      setNumeroFacturaSI(movimientoParaEditar.numero_factura || movimientoParaEditar.numero || '');

      // nuevos campos de banco
      setIdBanco(
        movimientoParaEditar.idbanco != null ? Number(movimientoParaEditar.idbanco) : null
      );
      setCostoBancario(
        movimientoParaEditar.costo_bancario != null
          ? String(movimientoParaEditar.costo_bancario)
          : ''
      );
      setNumeroComprobante(movimientoParaEditar.numero_comprobante || '');

      if (String(movimientoParaEditar.tipoDocumento || '').toUpperCase() === 'SI') {
        setDetalleEditCargado(true);
        return;
      }

      let activo = true;
      setCargandoEdicion(true);
      api
        .get(`/api/cartera/pago/${movimientoParaEditar.id}/editar`)
        .then(({ data }) => {
          if (!activo) return;
          const pago = data?.pago || {};
          setTerceroEdicion(pago.idtercero || null);
          const facturas = Array.isArray(data?.facturas) ? data.facturas : [];

          setTipoDocumento(
            (pago.tipoDocumento || movimientoParaEditar.tipoDocumento || 'PG').toUpperCase()
          );
          setFecha(
            toDateInputValue(pago.fecha) || toDateInputValue(movimientoParaEditar.fecha) || hoy
          );
          setValor(
            pago.valorTotal != null || pago.valor != null
              ? String(Number(pago.valorTotal ?? pago.valor))
              : movimientoParaEditar.valorTotal != null
                ? String(Number(movimientoParaEditar.valorTotal))
                : ''
          );
          setObservaciones(pago.observaciones || movimientoParaEditar.observaciones || '');
          setIdBanco(pago.idbanco != null ? Number(pago.idbanco) : null);
          setCostoBancario(
            pago.costo_bancario != null && pago.costo_bancario !== ''
              ? String(pago.costo_bancario)
              : ''
          );
          setNumeroComprobante(pago.numero_comprobante || '');
          setDetalleFacturasEdit(
            facturas.map((f) => ({
              idlink: f.idlink,
              idfactura: Number(f.idfactura),
              idpago_pp: f.idpago_pp || null,
              numero_factura: f.numero_factura || '',
              fecha: toDateInputValue(f.fecha),
              valorTotal: Number(f.valorTotal || 0),
              valorpago: Number(f.valorpago || 0),
              observaciones: f.observaciones || ''
            }))
          );
          setDetalleEditCargado(true);
          if (pago.tipoDocumento === 'PG' && facturas.some(f => f.idpago_pp)) {
            setValor(round2(facturas.reduce((s, f) => s + Number(f.valorpago || 0), 0)).toFixed(2));
          }
        })
        .catch((err) => {
          console.error('No se pudo cargar el detalle del pago:', err);
          if (activo) setDetalleEditCargado(false);
        })
        .finally(() => {
          if (activo) setCargandoEdicion(false);
        });

      return () => {
        activo = false;
      };
    }

    // ===== NUEVO MOVIMIENTO =====
    const defaultTipo = listaTipos[0] || 'PG';
    setTipoDocumento(defaultTipo);

    // Autocalcular por SALDO de facturas seleccionadas (para PG/ND/RT)
    const total = totalSaldoSeleccion(facturasSeleccionadas);
    const obs = facturasSeleccionadas
      .map((f) => f.numero_factura)
      .filter(Boolean)
      .join(', ');

    if (TIPOS_FACTURA.includes(defaultTipo)) {
      setValor(total > 0 ? total.toFixed(2) : '');
      setObservaciones(obs ? `Operación sobre facturas ${obs}` : '');
    } else {
      setValor('');
      setObservaciones('');
    }
  }, [
    show,
    esEdicion,
    movimientoParaEditar,
    facturasSeleccionadas,
    listaTipos,
    totalSaldoSeleccion
  ]);

  useEffect(() => {
    if (!show || esEdicion || !idSeleccionado) return;

    let activo = true;
    api
      .get('/api/cartera/prepagos', {
        params: { tipoMovimiento, idtercero: idSeleccionado }
      })
      .then(({ data }) => {
        if (!activo) return;
        const disponibleNc = (Array.isArray(data) ? data : [])
          .filter((p) => String(p.tipoDocumento || p.tipo || '').toUpperCase() === 'NC')
          .reduce((s, p) => s + Number(p.restante ?? p.disponible ?? 0), 0);
        setSaldoFavorNcDisponible(round2(disponibleNc));
      })
      .catch(() => {
        if (activo) setSaldoFavorNcDisponible(0);
      });

    return () => {
      activo = false;
    };
  }, [show, esEdicion, idSeleccionado, tipoMovimiento]);

  // ✅ Guarda el PDF preguntando dónde (si el navegador soporta)
  const _guardarPdfConPicker = async (blob, filename) => {
    // Chromium/Edge/Brave/Chrome (en https o localhost)
    if (window.showSaveFilePicker) {
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'PDF',
            accept: { 'application/pdf': ['.pdf'] }
          }
        ]
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    }
    return false;
  };

  const descargarComprobantePdf = async (idpago) => {
    if (!idpago) return;
    try {
      const res = await api.get(`/api/cartera/reporte-comprobante/${idpago}`, {
        params: { lado: String(tipoMovimiento || 'C').toUpperCase() },
        responseType: 'blob'
      });

      const blob = new Blob([res.data], { type: 'application/pdf' });
      const filename = `Comprobante_${idpago}.pdf`;

      // 1) Intentar preguntar dónde guardar (mejor opción)
      try {
        const guardado = await _guardarPdfConPicker(blob, filename);
        if (guardado) return;
      } catch (err) {
        // Si el usuario cancela el diálogo o el navegador bloquea, no rompemos
        // y caemos al método clásico.
        console.warn('Guardado con picker cancelado/no disponible:', err);
      }

      // 2) Fallback: descarga normal (depende del setting del navegador para preguntar ruta)
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('No se pudo descargar comprobante', e);
    }
  };

  // Autocalcular valor cuando cambia el tipo (por SALDO)
  useEffect(() => {
    if (esEdicion) return;
    const total = totalSaldoSeleccion(facturasSeleccionadas);
    if (TIPOS_FACTURA.includes(tipoDocumento)) {
      const saldoNc =
        tipoDocumento === 'PG' && usarSaldoFavorNc
          ? Math.min(total, saldoFavorNcDisponible)
          : 0;
      const cobroReal = round2(total - saldoNc);
      setValor(total > 0 ? cobroReal.toFixed(2) : '');
    } else setValor('');
  }, [
    tipoDocumento,
    facturasSeleccionadas,
    esEdicion,
    totalSaldoSeleccion,
    usarSaldoFavorNc,
    saldoFavorNcDisponible
  ]);

  const detalleFacturasVisible = useMemo(() => {
    if (esEdicion) return detalleFacturasEdit;
    return (facturasSeleccionadas || []).map((f) => ({
      idfactura: Number(f.idfactura || f.id || 0),
      numero_factura: f.numero_factura || f.numero || '',
      fecha: toDateInputValue(f.fecha),
      valorTotal: Number(f.valorTotal || f.valor_total || f.total || 0),
      valorpago: Number(f.valorpago || 0),
      observaciones: f.observaciones || ''
    }));
  }, [esEdicion, detalleFacturasEdit, facturasSeleccionadas]);

  const totalDetalleFacturas = useMemo(
    () => round2(detalleFacturasVisible.reduce((s, f) => s + Number(f.valorpago || 0), 0)),
    [detalleFacturasVisible]
  );
  const esCruceAnticipo = esEdicion && tipoDocumento === 'PG' && detalleFacturasEdit.some(f => f.idpago_pp);
  const saldoFavorNcAplicado =
    !esEdicion && tipoDocumento === 'PG' && usarSaldoFavorNc
      ? round2(Math.min(totalDetalleFacturas, saldoFavorNcDisponible))
      : 0;

  const actualizarValorDetalleFactura = (target, raw) => {
    const limpio = raw === '' ? '' : Math.max(0, Number(raw || 0));
    setDetalleFacturasEdit((prev) => {
      const next = prev.map((f) =>
        String(f.idlink || f.idfactura) === String(target) ? { ...f, valorpago: limpio } : f
      );
      const total = round2(next.reduce((s, f) => s + Number(f.valorpago || 0), 0));
      setValor(total > 0 ? total.toFixed(2) : '');
      return next;
    });
  };

  const valorEsSoloLectura = useMemo(
    () =>
      (!esEdicion && TIPOS_FACTURA.includes(tipoDocumento)) ||
      (esEdicion && TIPOS_FACTURA.includes(tipoDocumento) && detalleFacturasEdit.length > 0),
    [esEdicion, tipoDocumento, detalleFacturasEdit.length]
  );

  const isPagoOAnticipo = tipoDocumento === 'PG' || tipoDocumento === 'PP';
  const textoTipoPago = `${observaciones || ''} ${numeroComprobante || ''}`
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  const esCheque = /\bcheque\b|\bcheq\b|\bchq\b|\bch\s*#?/.test(textoTipoPago);
  const etiquetaNumeroComprobante = esCheque ? 'Cheque N°:' : 'Comprobante Bancario N°:';
  const placeholderNumeroComprobante = esCheque ? 'N° de cheque' : 'Ref/Comprobante de banco';

  // ===== SI: importar =====
  const validarImport = async () => {
    if (!archivoImport) return alert('Adjunta un archivo (.xlsx/.xls/.csv)');
    setImportando(true);
    setResultadoImport(null);
    try {
      const fd = new FormData();
      fd.append('file', archivoImport);
      const { data } = await api.post(`/api/cartera/si-import/${idSeleccionado}`, fd, {
        params: { tipoMovimiento, dry_run: 1, on_duplicate: 'skip' },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResultadoImport(data);
    } catch (e) {
      console.error(e);
      alert('❌ Error al validar el archivo');
    } finally {
      setImportando(false);
    }
  };

  const importarArchivo = async () => {
    if (!archivoImport) return alert('Adjunta un archivo');
    if (!window.confirm('¿Importar saldos iniciales para este tercero?')) return;
    setImportando(true);
    setResultadoImport(null);
    try {
      const fd = new FormData();
      fd.append('file', archivoImport);
      const { data } = await api.post(`/api/cartera/si-import/${idSeleccionado}`, fd, {
        params: { tipoMovimiento, dry_run: 0, on_duplicate: 'skip' },
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('✅ Importación completa');
      setResultadoImport(data);
      onClose?.();
    } catch (e) {
      console.error(e);
      alert('❌ Error al importar');
    } finally {
      setImportando(false);
    }
  };

  // ===== Guardar (sin NC) =====
  const guardar = async () => {
    if(guardandoPO)return;
    if(modoPO) {
      if(guardandoPO)return;
      if(!facturasPO.length||!(totalPO>0))return alert('Selecciona los PO que vas a pagar.');
      setGuardandoPO(true);
      try {
        const { data } = await api.post('/api/cartera/trazabilidad-po/nuevo-pago',{
          idtercero:idSeleccionado,fecha,idbanco:idBanco,numero_comprobante:numeroComprobante,
          costo_bancario:costoBancario,observaciones,facturas:facturasPO
        });
        await descargarComprobantePdf(data.idpago);
        alert('✅ Pago y PO registrados');onClose?.();
      }catch(err){alert(err.response?.data?.error||'No se pudo registrar el pago por PO.');}
      finally{setGuardandoPO(false);}
      return;
    }

    try {
      const obsFinal = beautify(observaciones);
      const esPagoOAnticipo = tipoDocumento === 'PG' || tipoDocumento === 'PP';

      // ===== EDICIÓN =====
      if (esEdicion && movimientoParaEditar) {
        if (!fecha) return alert('Ingresa fecha');
        if (cargandoEdicion) return alert('Espera a que termine de cargar el detalle.');
        if (TIPOS_FACTURA.includes(tipoDocumento) && !detalleEditCargado) {
          return alert('No se pudo cargar el detalle del pago. Cierra y vuelve a abrir.');
        }

        if (tipoDocumento === 'SI') {
          if (!numeroFacturaSI.trim()) return alert('Numero de factura es obligatorio');
          if (!valor || Number(valor) <= 0) return alert('Valor debe ser > 0');

          await api.put(`/api/cartera/saldo-inicial/${movimientoParaEditar.id}`, {
            tipoMovimiento,
            idtercero: idSeleccionado,
            tipoDocumento: 'SI',
            fecha,
            valorTotal: parseFloat(valor || 0),
            observaciones: obsFinal,
            numero_factura: numeroFacturaSI.trim()
          });

          alert('Movimiento actualizado');
          onClose?.();
          return;
        }

        const detallePago =
          TIPOS_FACTURA.includes(tipoDocumento) && detalleEditCargado
            ? detalleFacturasEdit
                .map((f) => ({
                  idfactura: Number(f.idfactura || 0),
                  valorpago: Number(f.valorpago || 0),
                  idpago_pp: f.idpago_pp || null
                }))
                .filter((f) => f.idfactura && f.valorpago > 0)
            : null;
        const valorEdit =
          detallePago && detallePago.length
            ? round2(detallePago.reduce((s, f) => s + Number(f.valorpago || 0), 0))
            : parseFloat(valor || 0);

        await api.put(`/api/cartera/pago/${movimientoParaEditar.id}`, {
          tipoMovimiento,
          idtercero: terceroEdicion || idSeleccionado,
          tipoDocumento,
          fecha,
          valorTotal: valorEdit,
          observaciones: obsFinal,
          ...(detallePago ? { facturas: detallePago } : {}),
          ...(esPagoOAnticipo
            ? {
                idbanco: idBanco || null,
                costo_bancario: parseFloat(costoBancario || 0),
                numero_comprobante: (numeroComprobante || '').trim() || null
              }
            : {})
        });

        if (autoDescargar && movimientoParaEditar.id) {
          await descargarComprobantePdf(movimientoParaEditar.id);
        }
        alert('✅ Movimiento actualizado');
        onClose?.();
        return;
      }

      if (!fecha) return alert('Ingresa fecha');

      // ===== TIPOS LIBRES (PP / SI) =====
      if (TIPOS_LIBRES.includes(tipoDocumento)) {
        // --- PP (Prepago) ---
        if (tipoDocumento === 'PP') {
          if (!valor || Number(valor) <= 0) return alert('Valor debe ser > 0');

          const { data } = await api.post('/api/cartera/pago-prepago', {
            tipoMovimiento,
            idtercero: idSeleccionado,
            fecha,
            valorTotal: parseFloat(valor),
            observaciones: obsFinal,
            idbanco: idBanco || null,
            costo_bancario: parseFloat(costoBancario || 0),
            numero_comprobante: (numeroComprobante || '').trim() || null
          });

          const idpago = data?.idpago;
          if (autoDescargar && idpago) {
            await descargarComprobantePdf(idpago);
          }

          alert('✅ Prepago registrado');
          onClose?.();
          return;
        }

        // --- SI (Saldo inicial) ---
        if (tipoDocumento === 'SI') {
          if (usarImportador) return alert('Estás en modo importación. Usa Validar/Importar.');
          if (!numeroFacturaSI.trim()) return alert('Número de factura es obligatorio');
          if (!valor || Number(valor) <= 0) return alert('Valor debe ser > 0');

          await api.post('/api/cartera/pago-completo', {
            tipoMovimiento,
            tipoDocumento: 'SI',
            idtercero: idSeleccionado,
            fecha,
            valorTotal: parseFloat(valor),
            observaciones: obsFinal,
            facturas: [],
            numero_factura: numeroFacturaSI.trim()
          });

          alert('✅ Saldo inicial registrado');
          onClose?.();
          return;
        }
      }

      // ===== TIPOS LIGADOS A FACTURAS (PG / ND / RT) =====
      const facturasValidas = facturasSeleccionadas.filter(
        (f) => f.idfactura && f.valorpago != null && f.valorpago > 0
      );
      if (facturasValidas.length === 0) {
        return alert('Selecciona facturas con valor para este tipo de movimiento.');
      }

      const { data } = await api.post('/api/cartera/pago-completo', {
        tipoMovimiento,
        tipoDocumento,
        idtercero: idSeleccionado,
        fecha,
        valorTotal: parseFloat(valor || 0),
        observaciones: obsFinal,
        facturas: facturasValidas,
        ...(tipoDocumento === 'PG'
          ? {
              usar_saldo_favor_nc: usarSaldoFavorNc,
              idbanco: idBanco || null,
              costo_bancario: parseFloat(costoBancario || 0),
              numero_comprobante: (numeroComprobante || '').trim() || null
            }
          : {})
      });

      const idpago = data?.idpago;
      if (autoDescargar && idpago) {
        await descargarComprobantePdf(idpago);
      }

      alert('✅ Movimiento registrado');
      onClose?.();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || '❌ Error al guardar el movimiento');
    }
  };

  if (!show) return null;

  const obsPlaceholder =
    {
      PG: 'Ej.: Transferencia, Banco Pichincha, Ref: 12345…',
      ND: 'Ej.: Ajuste por servicios adicionales…',
      RT: 'Ej.: Retención IVA/Fuente, comprobante…',
      PP: 'Ej.: Anticipo aplicado en futuras facturas…',
      SI: 'Ej.: Saldo inicial por migración (periodo)…'
    }[tipoDocumento] || 'Escribe detalles breves…';

  return (
    <div className="modal-overlay">
      <div className="modal modal-mov">
        <h3 style={{ marginBottom: 10 }}>{esCruceAnticipo ? 'Editar cruce de anticipo / saldo a favor' : titulo}</h3>

        <div style={styles.two}>
          <label style={styles.label}>
            Fecha:
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              style={styles.input}
            />
          </label>
          <label style={styles.label}>
            Tipo de Documento:
            <select
              value={tipoDocumento}
              disabled={esCruceAnticipo}
              onChange={(e) => setTipoDocumento(e.target.value)}
              style={styles.input}
            >
              {listaTipos.includes('PG') && <option value="PG">Pago</option>}
              {listaTipos.includes('ND') && <option value="ND">Nota Débito</option>}
              {listaTipos.includes('RT') && <option value="RT">Retención</option>}
              {listaTipos.includes('SI') && <option value="SI">Saldo Inicial</option>}
              {listaTipos.includes('PP') && <option value="PP">Prepago</option>}
            </select>
          </label>
        </div>

        {/* Banco + Costo bancario + Nº Comprobante (solo PG/PP) */}
        {isPagoOAnticipo && !esCruceAnticipo && (
          <>
            <div style={{ ...styles.two, marginTop: 8 }}>
              <label style={styles.label}>
                Banco:
                <select
                  value={idBanco ?? ''}
                  onChange={(e) => setIdBanco(e.target.value ? Number(e.target.value) : null)}
                  style={styles.input}
                >
                  <option value="">— Selecciona banco —</option>
                  {bancos.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.valor}
                    </option>
                  ))}
                </select>
              </label>

              <label style={styles.label}>
                Costo bancario:
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={costoBancario}
                  onChange={(e) => setCostoBancario(e.target.value)}
                  placeholder="0.00"
                  style={styles.number}
                />
              </label>
            </div>

    {tipoMovimiento==='C' && tipoDocumento==='PG' && !esEdicion && facturasSeleccionadas.length>0 && (
 <div><label><input type="checkbox" checked={usarPO} disabled={guardandoPO} onChange={e=>setUsarPO(e.target.checked)}/> Elegir PO para este pago (opcional)</label>
 {modoPO && <p>Este pago registra el importe de los PO elegidos. Para combinar saldo a favor de notas de crédito, utiliza el pago normal.</p>}
 {modoPO && <SeleccionPOPago facturas={facturasSeleccionadas} onChange={setFacturasPO}/>}</div>)}
        <label style={{ ...styles.label, marginTop: 8 }}>
              {etiquetaNumeroComprobante}
              <input
                type="text"
                value={numeroComprobante}
                onChange={(e) => setNumeroComprobante(e.target.value)}
                placeholder={placeholderNumeroComprobante}
                style={styles.input}
              />
            </label>
          </>
        )}

        {tipoDocumento === 'SI' && !usarImportador && (
          <label style={{ ...styles.label, marginTop: 8 }}>
            Número de Factura (obligatorio):
            <input
              type="text"
              value={numeroFacturaSI}
              onChange={(e) => setNumeroFacturaSI(e.target.value)}
              placeholder="Ej. SI-0001"
              style={styles.input}
            />
          </label>
        )}

        <label style={{ ...styles.label, marginTop: 8 }}>
          {!modoPO && tipoDocumento === 'PG' && saldoFavorNcAplicado > 0
            ? 'Valor a cobrar / recibir:'
            : 'Valor Total:'}
          <input
            type="number"
            value={modoPO ? totalPO.toFixed(2) : valor}
            onChange={(e) => setValor(e.target.value)}
            readOnly={modoPO || valorEsSoloLectura}
            placeholder={
              !esEdicion && TIPOS_FACTURA.includes(tipoDocumento)
                ? 'Autocalculado por saldos de facturas seleccionadas'
                : ''
            }
            style={styles.number}
          />
        </label>

        {!modoPO && !esEdicion && tipoDocumento === 'PG' && saldoFavorNcDisponible > 0 && (
          <div
            style={{
              marginTop: 8,
              padding: '9px 11px',
              borderRadius: 8,
              border: '1px solid #b9dfc6',
              background: '#f2fbf5',
              fontSize: 13
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={usarSaldoFavorNc}
                onChange={(e) => setUsarSaldoFavorNc(e.target.checked)}
              />
              Usar saldo a favor de notas de crédito
            </label>
            <div style={{ marginTop: 5 }}>
              Disponible: <b>${saldoFavorNcDisponible.toFixed(2)}</b>
              {' · '}Aplicado en este cobro: <b>${saldoFavorNcAplicado.toFixed(2)}</b>
              {' · '}Total facturas: <b>${totalDetalleFacturas.toFixed(2)}</b>
            </div>
          </div>
        )}

        {TIPOS_FACTURA.includes(tipoDocumento) && (cargandoEdicion || detalleFacturasVisible.length > 0) && (
          <div className="mmov-card" style={{ marginTop: 8 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                marginBottom: 8
              }}
            >
              <strong>Detalle de facturas ({detalleFacturasVisible.length})</strong>
              <span style={{ fontSize: 13 }}>
                Total detalle: ${totalDetalleFacturas.toFixed(2)}
              </span>
            </div>
            {esCruceAnticipo && <p style={styles.hint}>Puedes corregir la fecha y los importes aplicados. Reducir un importe devuelve saldo al anticipo; eliminar el movimiento libera todas sus facturas. El anticipo original se conserva.</p>}

            {cargandoEdicion ? (
              <div style={styles.hint}>Cargando detalle...</div>
            ) : (
              <div className="mmov-facturas-scroll">
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 6 }}>
                        Factura
                      </th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: 6 }}>
                        Fecha
                      </th>
                      <th style={{ textAlign: 'right', borderBottom: '1px solid #ddd', padding: 6 }}>
                        Valor aplicado
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalleFacturasVisible.map((f) => (
                      <tr key={`${f.idfactura}-${f.idlink || ''}`}>
                        <td style={{ padding: 6 }}>{f.numero_factura || f.idfactura}{f.idpago_pp ? <div style={styles.hint}>Saldo a favor #{f.idpago_pp}</div> : null}</td>
                        <td style={{ padding: 6 }}>{f.fecha || ''}</td>
                        <td style={{ padding: 6, textAlign: 'right' }}>
                          {esEdicion ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={f.valorpago}
                              onChange={(e) =>
                                actualizarValorDetalleFactura(f.idlink || f.idfactura, e.target.value)
                              }
                              style={{ ...styles.number, width: 130 }}
                            />
                          ) : (
                            `$${Number(f.valorpago || 0).toFixed(2)}`
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Observaciones */}
        <div style={{ ...styles.grid, marginTop: 8 }}>
          <label style={styles.label}>
            Observaciones:
            <textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value.slice(0, MAX_OBS))}
              onBlur={(e) => setObservaciones(beautify(e.target.value))}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === 'Enter') {
                  e.preventDefault();
                  guardar();
                }
              }}
              rows={3}
              placeholder={obsPlaceholder}
              maxLength={MAX_OBS}
              style={styles.textarea}
            />
          </label>
          <div style={styles.hintRow}>
            <div style={styles.hint}>
              Tip: usa los chips para completar más rápido; <b>Ctrl + Enter</b> para guardar.
            </div>
            <div style={styles.hint}>
              {observaciones.length}/{MAX_OBS}
            </div>
          </div>
          <div style={styles.chips}>
            {chips.map((c) => (
              <button key={c} type="button" style={styles.chip} onClick={() => insertSnippet(c)}>
                {c}
              </button>
            ))}
          </div>
          <div style={styles.preview}>
            📝 <b>Vista previa:</b> {beautify(observaciones) || '— Sin observaciones —'}
          </div>
        </div>

        {/* Importador SI */}
        {tipoDocumento === 'SI' && (
          <div className="mmov-card">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={usarImportador}
                onChange={(e) => setUsarImportador(e.target.checked)}
              />
              Importar desde Excel (para este tercero)
            </label>
            {usarImportador && (
              <div style={{ marginTop: 8 }}>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => setArchivoImport(e.target.files?.[0] || null)}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button disabled={importando || !archivoImport} onClick={validarImport}>
                    🔎 Validar (simular)
                  </button>
                  <button disabled={importando || !archivoImport} onClick={importarArchivo}>
                    ⬆️ Importar
                  </button>
                </div>
                {resultadoImport && (
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    <b>Resultado:</b> {resultadoImport.dry_run ? '(simulación)' : '(importado)'} ·
                    insertados: {resultadoImport.insertados || 0} · simulados:{' '}
                    {resultadoImport.ok_simulados || 0} · saltados: {resultadoImport.saltados || 0}{' '}
                    · errores: {resultadoImport.errores || 0}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Botonera */}
        <div
          className="modal-buttons"
          style={{
            marginTop: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={autoDescargar}
              onChange={(e) => setAutoDescargar(e.target.checked)}
            />
            Preguntar dónde guardar PDF al guardar
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {!usarImportador && (
              <button disabled={guardandoPO} onClick={guardar}>{esEdicion ? '💾 Actualizar' : '✅ Guardar'}</button>
            )}
            <button onClick={onClose}>❌ Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalMovimiento;
